import { FormControl, MenuItem, Select, Typography, MenuProps } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useEffect } from 'react'
import { deepPurple } from '@material-ui/core/colors'
import { useFiltersContext } from '../../contexts/Filters'
import { Filters } from '../../../types/general'
import { Region } from '../../../types/region'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    width: '100%',
    flexWrap: 'wrap',
    margin: '100px 0 50px 0',
    [theme.breakpoints.down('md')]: {
      marginTop: '150px',
    },
  },
  filterLabel: {
    marginRight: '20px',
    fontSize: '20px',
    letterSpacing: '0',
  },
  currencyIcon: {
    marginRight: '10px',
  },
  purpleText: {
    color: theme.palette.primary.main,
    fontSize: '16px',
    margin: '0 0 0 auto',
  },
  selected: {
    borderColor: `${theme.palette.primary.main} !important`,
  },
  menuItem: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '28px',
    '&:hover': {
      opacity: 0.8,
      transition: 'opacity .2s ease-out',
      '-moz-transition': 'opacity .2s ease-out',
      '-webkit-transition': 'opacity .2s ease-out',
      '-o-transition': 'opacity .2s ease-out',
    },
  },
  formControl: {
    height: '58px',
    '& .MuiInputBase-root': {
      height: '58px',
    },
    [theme.breakpoints.down('md')]: {
      margin: '20px 0',
    },
  },
  gutters: {},
  input: {
    color: theme.palette.secondary.main,
    fontWeight: 600,
  },
  selectIcon: {
    color: theme.palette.primary.main,
  },
  button: {
    height: '58px',
    width: '250px',
    margin: '0 0 0 auto',
    '& h6': {
      fontSize: '18px',
    },
    [theme.breakpoints.down('md')]: {
      margin: '10px 0',
    },
  },
  inputContainer: {
    width: '100%',
    margin: '0 10px',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  titleContainer: {
    left: '-10px',
    position: 'relative',
    width: 'fit-content',
    '& h5': {
      fontSize: '16px',
      color: theme.palette.primary.main,
      position: 'relative',
    },
  },
  titleBackground: {
    position: 'absolute',
    top: '10px',
    left: '-5px',
    width: '100%',
    backgroundColor: theme.palette.secondary.main,
    height: '18px',
    zIndex: -1,
  },
  paper: {
    borderRadius: 12,
    marginTop: 8,
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
    background: 'white',
    fontSize: '1rem',
    '& li': {
      fontWeight: 200,
      paddingTop: 12,
      paddingBottom: 12,
      fontSize: '.8rem',
    },
    '& li:hover': {
      background: deepPurple[100],
    },
    '& li.Mui-selected': {
      color: 'white',
      background: deepPurple[400],
      fontWeight: 600,
    },
    '& li.Mui-selected:hover': {
      background: deepPurple[500],
    },
  },
  select: {
    width: '100%',
    background: 'white',
    color: deepPurple[500],
    fontWeight: 600,
    border: '1px solid #e0e0e0',
    borderWidth: 2,
    borderRadius: 12,
    padding: '.4rem .8rem',
    fontSize: '1rem',
    minWidth: '7rem',
    '&:focus': {
      borderRadius: 12,
      background: 'white',
      borderColor: deepPurple[100],
    },
  },
  selectContainer: {
    minWidth: '4rem',
  },
  valueContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    marginRight: '.2rem',
  },
  value: {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    color: 'black',
    textTransform: 'capitalize',
  },
}))

interface Props {
  regions: Region[]
  onFilter: (filters: Filters) => void
}

export default function CryptoCardFilters({ regions, onFilter }: Props): JSX.Element {
  const classes = useStyles({})
  const selectProps = {
    classes: {
      paper: classes.paper,
      list: classes.list,
    },
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  } as MenuProps
  const context = useFiltersContext()
  const selectedRegion = context.filters?.region

  function onRegionChange(
    event: React.ChangeEvent<{
      name?: string | undefined
      value: unknown
    }>,
  ) {
    const foundRegion = regions.find((obj) => obj.name === event.target.value)
    if (foundRegion) {
      context.setFilter({
        region: foundRegion,
      })
    }
  }

  useEffect(() => {
    if (onFilter) {
      onFilter({
        region: selectedRegion,
      })
    }
  }, [selectedRegion])

  return (
    <div className={classes.container}>
      <div className={classes.inputContainer}>
        <div className={classes.titleContainer}>
          <div className={classes.titleBackground} />
          <Typography variant="subtitle1" className={classes.filterLabel}>
            Compare crypto cards in
          </Typography>{' '}
        </div>

        <div className={classes.selectContainer}>
          <FormControl>
            <Select
              onChange={onRegionChange}
              classes={{ icon: classes.selectIcon, root: classes.select }}
              value={selectedRegion?.name}
              id="select-crypto-card-region"
              MenuProps={selectProps}
              disableUnderline
              renderValue={() => (
                <span className={classes.valueContainer}>
                  <Typography variant="body2" className={classes.value}>
                    {selectedRegion?.name}
                  </Typography>
                </span>
              )}
            >
              {regions.map((region) => (
                <MenuItem value={region.name} key={region.name}>
                  {region.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>
    </div>
  )
}
